import React, { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { Container, Typography, TextField, Button, Paper, Box, Link, Alert } from '@mui/material';
import Grid from '../components/common/Grid';
import { Formik, Form, Field, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import AuthService from '../services/auth.service';

interface SignupFormValues {
  email: string;
  password: string;
  confirmPassword: string;
  first_name: string;
  last_name: string;
  organization: string;
  description: string;
  consent_accepted: boolean;
}

const SignupSchema = Yup.object().shape({
  email: Yup.string()
    .email('Invalid email address')
    .required('Email is required'),
  password: Yup.string()
    .required('Password is required')
    .min(6, 'Password must be at least 6 characters'),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('password')], 'Passwords must match')
    .required('Confirm password is required'),
  first_name: Yup.string()
    .required('First name is required'),
  last_name: Yup.string()
    .required('Last name is required'),
  organization: Yup.string()
    .required('Organization is required'),
  description: Yup.string()
    .required('Please provide a brief description'),
  consent_accepted: Yup.boolean()
});

const SignupPage: React.FC = () => {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const initialValues: SignupFormValues = {
    email: '',
    password: '',
    confirmPassword: '',
    first_name: '',
    last_name: '',
    organization: '',
    description: '',
    consent_accepted: false,
  };

  const handleSubmit = async (
    values: SignupFormValues,
    { setSubmitting }: FormikHelpers<SignupFormValues>
  ) => {
    try {
      setError(null);
      const { confirmPassword, ...signupData } = values;
      const response = await AuthService.signup(signupData);
      
      if (response.status === 'pending_confirmation') {
        setSuccess('Your account has been created! Please check your email to confirm your registration.');
      }
    } catch (err: any) {
      if (err.response && err.response.data) {
        setError(err.response.data.detail || 'Registration failed. Please try again.');
      } else {
        setError('An error occurred. Please try again later.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // If successfully registered, show success message
  if (success) {
    return (
      <Container maxWidth="sm">
        <Paper elevation={3} sx={{ p: 4, mt: 8 }}>
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
          <Typography variant="body1" paragraph>
            We've sent a confirmation email to your address. Please check your inbox and click on the confirmation link to activate your account.
          </Typography>
          <Button
            fullWidth
            variant="outlined"
            color="primary"
            onClick={() => navigate('/login')}
            sx={{ mt: 2 }}
          >
            Back to Login
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <Paper elevation={3} sx={{ p: 4, mt: 8 }}>
        <Typography variant="h4" component="h1" align="center" gutterBottom>
          Sign Up for EcoActionAI
        </Typography>
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        <Formik
          initialValues={initialValues}
          validationSchema={SignupSchema}
          onSubmit={handleSubmit}
        >
          {({ errors, touched, isSubmitting }) => (
            <Form>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Field
                    as={TextField}
                    fullWidth
                    margin="normal"
                    id="first_name"
                    name="first_name"
                    label="First Name"
                    error={touched.first_name && Boolean(errors.first_name)}
                    helperText={touched.first_name && errors.first_name}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Field
                    as={TextField}
                    fullWidth
                    margin="normal"
                    id="last_name"
                    name="last_name"
                    label="Last Name"
                    error={touched.last_name && Boolean(errors.last_name)}
                    helperText={touched.last_name && errors.last_name}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Field
                    as={TextField}
                    fullWidth
                    margin="normal"
                    id="email"
                    name="email"
                    label="Email Address"
                    type="email"
                    error={touched.email && Boolean(errors.email)}
                    helperText={touched.email && errors.email}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Field
                    as={TextField}
                    fullWidth
                    margin="normal"
                    id="password"
                    name="password"
                    label="Password"
                    type="password"
                    error={touched.password && Boolean(errors.password)}
                    helperText={touched.password && errors.password}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Field
                    as={TextField}
                    fullWidth
                    margin="normal"
                    id="confirmPassword"
                    name="confirmPassword"
                    label="Confirm Password"
                    type="password"
                    error={touched.confirmPassword && Boolean(errors.confirmPassword)}
                    helperText={touched.confirmPassword && errors.confirmPassword}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Field
                    as={TextField}
                    fullWidth
                    margin="normal"
                    id="organization"
                    name="organization"
                    label="Organization"
                    error={touched.organization && Boolean(errors.organization)}
                    helperText={touched.organization && errors.organization}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Field
                    as={TextField}
                    fullWidth
                    margin="normal"
                    id="description"
                    name="description"
                    label="Description (How will you use EcoActionAI?)"
                    multiline
                    rows={4}
                    error={touched.description && Boolean(errors.description)}
                    helperText={touched.description && errors.description}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                    <Field
                      as="input"
                      type="checkbox"
                      id="consent_accepted"
                      name="consent_accepted"
                      style={{ marginRight: '8px' }}
                    />
                    <label htmlFor="consent_accepted" style={{ fontSize: '0.875rem', color: '#B0B0B0' }}>
                      I agree to allow EcoActionAI to collect and use my data for improving the service. 
                      Details of this are available at{' '}
                      <a
                        href="https://www.csiro.au/Privacy"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: '#68c8de' }}
                      >
                        https://www.csiro.au/Privacy
                      </a>.
                    </label>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    color="primary"
                    size="large"
                    disabled={isSubmitting}
                    sx={{ mt: 3, mb: 2 }}
                  >
                    {isSubmitting ? 'Signing up...' : 'Sign Up'}
                  </Button>
                </Grid>
              </Grid>
              
              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Typography variant="body2" display="inline">
                  Already have an account?{' '}
                </Typography>
                <Link component={RouterLink} to="/login" variant="body2">
                  Login
                </Link>
              </Box>
            </Form>
          )}
        </Formik>
      </Paper>
    </Container>
  );
};

export default SignupPage;
